import { Component, ElementRef, EventEmitter, forwardRef, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Traveller, RoomGuestIndex } from '../../model/checkout/guest-info.model';

@Component({
  selector: 'act-form-autocomplete',
  templateUrl: './form-autocomplete.component.html',
  styleUrls: ['./form-autocomplete.component.scss']
  ,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormAutocompleteComponent),
      multi: true
    }
  ]
})
export class FormAutocompleteComponent implements OnInit, ControlValueAccessor, OnChanges {

  @Input() pageId: string;
  @Input() options: any | Traveller[];
  @Input() className: string;
  @Input() placeholder: string;
  @Input() owned: RoomGuestIndex;
  @Input() filterOn: any[] = ['fullName'];
  @Input() invalid = false;
  @Input() removable = true;
  @Input() displayOwner = false;
  @Input() disabled = false;
  @Input() isLoading = false;
  @Input() displayNoResult = false;

  @Output() onSelect: EventEmitter<Traveller> = new EventEmitter();
  @Output() onDeselect: EventEmitter<any> = new EventEmitter();
  @Output() newValueInserted: EventEmitter<any> = new EventEmitter();
  @Output() onRemove: EventEmitter<any> = new EventEmitter();
  @Output() onBlurInput: EventEmitter<any> = new EventEmitter();

  @ViewChild('inputEl') inputElement: ElementRef;

  onChange: Function;
  onTouch: Function;

  data: any | Traveller;
  displayDropdown = false;
  optionIndex = {
    selecting: -1,
    submit: -1
  };
  optionSelected = Traveller;
  filteredList: any[] = [];
  isDropUp = false;
  ownerStr = '';
  value: string;

  writeValue(value: any) {
    this.value = value;
  }

  registerOnChange(onChange: Function) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouch: Function) {
    this.onTouch = onTouch;
  }

  constructor(
    private elementRef: ElementRef,
  ) {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.options && changes.options.currentValue) {
      this.getFilteredList();
    }
    if (changes.owned && changes.owned.currentValue) {
      this.ownerStr = `${this.owned.roomIndex},${this.owned.guestIndex}`;
      if (this.isIndexValid(this.optionIndex.submit)) {
        this.findSubmit('owned', this.ownerStr);
      } else {
        if (this.options) {
          const owner = this.options.find(option => (option.owned === this.ownerStr));
          if (owner) {
            this.optionIndex.submit = owner.userGuestID;
          }
        }
      }
    }
    if (changes.pageId && changes.pageId.currentValue) {
      this.pageId = changes.pageId.currentValue;
    }
  }

  ngOnInit() {
    this.filteredList = this.options || [];
  }

  @HostListener('document:click', ['$event'])
  public onClickOutside(event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.displayDropdown && this.onTouch(true);
      this.displayDropdown = false;
    }
  }

  onSelectOption(byClick: any | Traveller = undefined) {
    this.displayDropdown = this.isLoading;
    let optionSelected;
    if (this.isIndexValid(this.optionIndex.submit)) {
      this.findSubmit('selected', false);
    }
    if (byClick) {
      optionSelected = this.options.find(option => (byClick.userGuestID === option.userGuestID));
    } else {
      optionSelected = this.options.find(option => (option.userGuestID === this.filteredList[this.optionIndex.selecting].userGuestID));
    }
    this.optionIndex.submit = optionSelected.userGuestID;
    optionSelected.selected = true;
    optionSelected.owned = this.ownerStr;
    this.data = optionSelected;
    this.value = this.data.fullName;

    this.onSelect.emit(this.data);
  }

  onFocus($event) {
    const dropdownContainer = $event.currentTarget;
    this.filteredList = this.options ? this.filter(this.options, this.value, this.filterOn) : [];

    const position = dropdownContainer.getBoundingClientRect().top;
    const buttonHeight = dropdownContainer.getBoundingClientRect().height;

    const dropdownMenu = dropdownContainer.parentElement.getElementsByClassName('dropdown');
    if (dropdownMenu[0]) {
      const menuHeight = dropdownMenu[0].offsetHeight;

      const windowHeight = window.innerHeight;
      if (position > menuHeight && windowHeight - position < buttonHeight + menuHeight) {
        this.isDropUp = true;
      } else {
        this.isDropUp = false;
      }
      this.displayDropdown = true;
      this.checkDisplayDropDown();
    }
    this.getFilteredList();
  }

  reselecting(index: number = undefined, submit: boolean = false) {
    this.filteredList.map(option => (option.selecting = false));
    if (index !== undefined) {
      this.optionIndex.selecting = index;
      submit && (this.optionIndex.submit = index);
      if (this.isIndexValid(this.optionIndex.selecting)) {
        this.filteredList[this.optionIndex.selecting].selecting = true;
      }
    }
  }

  deselect() {
    this.options.map(option => (option.selected = false));
    this.onDeselect.emit(false);
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.keyCode === 38 || event.keyCode === 40) {
      // this.handleArrowKey(event.key);
    } else if (event.key === 'Enter') {
      this.isIndexValid(this.optionIndex.selecting) && (this.onSelectOption());
    } else if (event.keyCode === 9) {
      this.onBlur();
    }
  }

  handleArrowKey(dir: string) {
    const isDown = dir === 'ArrowDown';
    do {
      if (isDown ? this.optionIndex.selecting + 1 < this.filteredList.length : this.optionIndex.selecting - 1 >= 0) {
        isDown ? this.optionIndex.selecting++ : this.optionIndex.selecting--;
      }
    } while (this.filteredList[this.optionIndex.selecting].selected);
    this.reselecting(this.optionIndex.selecting);
  }

  onBlur(checkIndex: boolean = false) {
    this.displayDropdown = false;
    checkIndex && this.reselecting(-1);
    this.handleNewValueInserted();
  }

  onValueChange() {
    this.onChange(this.value);
    this.onTouch(true);

    this.getFilteredList();
    this.displayDropdown = true;
    this.isIndexValid(this.optionIndex.selecting) && (this.reselecting(-1));
    if (this.isIndexValid(this.optionIndex.submit)) {
      this.findSubmit('selected', false);
      this.findSubmit('owned', undefined);
      this.reselecting(-1, true);
      this.deselect();
    }
    this.handleNewValueInserted();
  }

  onRemoved(option: Traveller) {
    if (this.isIndexValid(this.optionIndex.submit) &&
      this.optionIndex.submit === option.userGuestID) {
      this.reselecting(-1, true);
      this.onRemove.emit({ id: option.userGuestID, clearAll: true });
    } else {
      this.onRemove.emit({ id: option.userGuestID, clearAll: false });
    }
  }

  findIndex(val: Traveller) {
    return this.filteredList.findIndex(option => (option.userGuestID === val.userGuestID));
  }

  isIndexValid(index: number) {
    return index >= 0;
  }

  handleNewValueInserted() {
    this.newValueInserted.emit(this.isIndexValid(this.optionIndex.submit));
  }

  getFilteredList() {
    this.filteredList = this.options ? this.filter(this.options, this.value, this.filterOn) : [];
    this.checkDisplayDropDown();
  }

  checkDisplayDropDown() {
    if ((this.filteredList.length === 0 && !this.displayNoResult) || (this.filteredList.length === 1 && this.filteredList[0].selected)) {
      this.displayDropdown = false;
    }
  }

  findSubmit(node: string, changeValue: any) {
    this.options.find(option => (option.userGuestID === this.optionIndex.submit))[`${node}`] = changeValue;
  }

  focus() {
    this.inputElement.nativeElement.focus();
  }

  filter(items: any[], search: string, nodes: string[]): any {
    if (!!!search) {
      return items;
    }
    const result = [];
    items && items.forEach(item => {
      for (const node of nodes) {
        if (item[node] && item[node].toLowerCase().includes(search.toLowerCase())) {
          result.push(item);
          break;
        }
      }
    });
    return result;
  }

  onBlurInputHandle(event) {
    if (event.target !== document.activeElement) {
      this.onBlurInput.emit(event);
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/components/form-autocomplete/form-autocomplete.component.ts